import _defineProperty from '@babel/runtime/helpers/defineProperty';
import _extends from '@babel/runtime/helpers/extends';
import React__default, { PureComponent, createRef } from 'react';
import PT from 'prop-types';
import { ThemeContext } from 'styled-components';
import { a as CardStyled, C as CardBodyStyled } from '../Card/Card.styles-BX0II1Bm.js';
import { W as Wrapper, T as TabGroupWrapper, a as TabGroup, b as TabRemainSpace, c as Tab, d as TabWrapper, D as DefaultText } from './TabbedCard.styles-C0oxOSX0.js';
import { t as theme } from '../theme/themes/default-BrkF3upi.js';
import { K as KEYBOARD_KEYNAMES } from '../LocalModules/storm-ui-utils/dist/esm/keyboardKeynames-AmBipDtf-AmBipDtf.js';
import { g as getFocusableElements } from '../LocalModules/storm-ui-utils/dist/esm/focusableElements/getFocusableElements-BjFtn5aB-BjFtn5aB.js';
import { s as scrollIntoBounds } from '../LocalModules/storm-ui-utils/dist/esm/scrollIntoBounds/scrollIntoBounds-Dhl2O567-Dhl2O567.js';
import { a as TaktIdProvider, T as TaktIdConsumer } from '../TaktIdContext/TaktIdContext-DlOuPWuU.js';
import { c as createStormTaktId } from '../TaktIdContext/TaktIdContext.utils-DaCk9T1Y.js';

// Used to add default text wrapper
// or isActive prop to custom components
const modifyTabChildren = (tabChildren, isActive = false) => {
  if (typeof tabChildren === 'string') {
    return /*#__PURE__*/React__default.createElement(DefaultText, {
      isActive: isActive,
      $label: tabChildren
    }, tabChildren);
  }
  if (/*#__PURE__*/React__default.isValidElement(tabChildren)) {
    const {
      type
    } = tabChildren;

    // WARNING...SMELLY CODE AHEAD
    if (
    // is a function component
    /createElement/.test(String(type))

    // is a class component
    || typeof type === 'function' && /react.element/.test(String(tabChildren.$$typeof))) {
      return /*#__PURE__*/React__default.cloneElement(tabChildren, {
        isActive
      });
    }
  }

  // don't modify if custom element is a standard dom element
  return tabChildren;
};
// React component representing the physical tab and its logic
const TabbedCardTabItem = props => {
  const {
    onChange,
    isActive,
    tabChildren,
    value,
    taktId,
    taktValue,
    ...itemProps
  } = props;
  const handleKeyUp = event => {
    const {
      key
    } = event;
    if (key === KEYBOARD_KEYNAMES.ENTER || key === KEYBOARD_KEYNAMES.SPACE) {
      onChange(value, event);
      event.preventDefault();
    }
  };
  const handleChange = event => {
    onChange(value, event);
  };
  return /*#__PURE__*/React__default.createElement(TaktIdConsumer, {
    taktId: taktId,
    taktValue: taktValue,
    fallbackId: createStormTaktId('tabbed-card-tab-item')
  }, ({
    getDataTaktAttributes
  }) => /*#__PURE__*/React__default.createElement(Tab, _extends({}, getDataTaktAttributes(), {
    type: "button",
    key: value,
    id: value,
    isActive: isActive,
    "aria-selected": isActive,
    role: "tab",
    tabIndex: isActive ? 0 : -1,
    onClick: handleChange,
    onKeyUp: handleKeyUp
  }, itemProps), /*#__PURE__*/React__default.createElement(TabWrapper, {
    $isActive: isActive
  }, modifyTabChildren(tabChildren, isActive))));
};
TabbedCardTabItem.propTypes = {
  /**
   * Denotes whether the tab is in an active state (selected) or not.
   */
  isActive: PT.bool,
  /**
   * This function returns the value of the tab when changed event is fired.
   */
  onChange: PT.func.isRequired,
  /**
   * The contents of the tab relating to this card. Any content may be passed
   * for custom formatting. We will pass isActive to any custom class or function
   * component, for use in special actions within those tabs based on state.
   * A string will give default styles.
   */
  tabChildren: PT.node.isRequired,
  /**
   * Must be a unique value for this tabbed card group. Used by parent to determine
   * which card is selected.
   */
  value: PT.string.isRequired
};
TabbedCardTabItem.defaultProps = {
  isActive: false
};
// React component representing the physical tabbed card and its logic
class TabbedCard extends PureComponent {
  constructor(props) {
    super(props);
    _defineProperty(this, "context", theme);
    _defineProperty(this, "navRef", /*#__PURE__*/createRef());
    _defineProperty(this, "tabGroupRef", /*#__PURE__*/createRef());
    // KeyDown events to handle for keyboard accessibility. Best practices for Tabs with manual
    // activation here: https://www.w3.org/TR/wai-aria-practices/examples/tabs/tabs-2/tabs.html
    _defineProperty(this, "handleKeyDown", event => {
      const {
        key
      } = event;

      // By default, buttons handle the Enter key on KeyDown. We prevent this because we want
      // to listen to KeyUp to prevent repeat triggers.
      if (key === KEYBOARD_KEYNAMES.ENTER) {
        event.preventDefault();
      }
      if (this.tabGroupRef.current) {
        const focusableEls = getFocusableElements(this.tabGroupRef.current);
        const firstFocusableEl = focusableEls[0];
        const lastFocusableEl = focusableEls[focusableEls.length - 1];

        // When "Left Arrow" or "Right Arrow" is pressed, move focus to the left or right tab
        if ((key === KEYBOARD_KEYNAMES.ARROW_RIGHT || key === KEYBOARD_KEYNAMES.ARROW_LEFT || key === KEYBOARD_KEYNAMES.HOME || key === KEYBOARD_KEYNAMES.END) && document.activeElement && document.activeElement instanceof HTMLElement) {
          const currentIndex = focusableEls.indexOf(document.activeElement);
          let newFocusIndex = currentIndex;

          // When "Right Arrow" is pressed, focus on the tab to the right
          if (key === KEYBOARD_KEYNAMES.ARROW_RIGHT) {
            newFocusIndex = currentIndex + 1;
            if (document.activeElement === lastFocusableEl) {
              newFocusIndex = 0;
            }
          }

          // When "Left Arrow" is pressed, focus on the tab to the left
          if (key === KEYBOARD_KEYNAMES.ARROW_LEFT) {
            newFocusIndex = currentIndex - 1;
            if (document.activeElement === firstFocusableEl) {
              newFocusIndex = focusableEls.length - 1;
            }
          }

          // When "End" is pressed, focus on the last tab in the tab group
          if (key === KEYBOARD_KEYNAMES.END) {
            newFocusIndex = focusableEls.length - 1;
          }

          // When "Home" is pressed, focus on the first tab in the tab group
          if (key === KEYBOARD_KEYNAMES.HOME) {
            newFocusIndex = 0;
          }
          focusableEls[newFocusIndex].focus();
          event.preventDefault();
        }
      }
    });
    this.state = {
      isTabOverflow: false
    };
  }
  componentDidMount() {
    var _this$tabGroupRef$cur, _this$tabGroupRef$cur2, _this$navRef$current;
    const tabsWidth = (_this$tabGroupRef$cur = this.tabGroupRef.current) === null || _this$tabGroupRef$cur === void 0 ? void 0 : _this$tabGroupRef$cur.scrollWidth;
    const tabsContainerWidth = (_this$tabGroupRef$cur2 = this.tabGroupRef.current) === null || _this$tabGroupRef$cur2 === void 0 ? void 0 : _this$tabGroupRef$cur2.offsetWidth;

    /* Set a isTabOverflow flag on whether to add the scroll indicator */
    if (tabsWidth && tabsContainerWidth && tabsWidth > tabsContainerWidth) {
      this.setState({
        isTabOverflow: true
      });
    }
    /* Scroll the active tab to be visible in the frame if required. */
    scrollIntoBounds(this.navRef.current, (_this$navRef$current = this.navRef.current) === null || _this$navRef$current === void 0 ? void 0 : _this$navRef$current.querySelector('[aria-selected=true][role="tab"]'));
  }
  render() {
    const {
      collapseHeaderWithOneTab,
      selectedTab,
      navChildren,
      onChange,
      children,
      padding,
      taktId,
      taktValue,
      ...rest
    } = this.props;
    const theme = this.context;
    const {
      isTabOverflow
    } = this.state;
    const paddingValue = padding || theme.card.tabs.paddingValue;
    let tabContent;
    const tabs = React__default.Children.map(children, child => {
      if (! /*#__PURE__*/React__default.isValidElement(child)) {
        return null;
      }
      const {
        value,
        tabChildren,
        children: itemChildren,
        ...itemRest
      } = child.props;
      const isActive = selectedTab === value;
      if (isActive) {
        tabContent = child;
      }
      return /*#__PURE__*/React__default.createElement(TabbedCardTabItem, _extends({
        onChange: onChange,
        isActive: isActive,
        tabChildren: tabChildren,
        value: value
      }, itemRest));
    });
    return /*#__PURE__*/React__default.createElement(TaktIdProvider, {
      taktId: taktId,
      taktValue: taktValue,
      fallbackId: createStormTaktId('tabbed-card')
    }, /*#__PURE__*/React__default.createElement(CardStyled, rest, /*#__PURE__*/React__default.createElement(Wrapper, {
      isTabOverflow: isTabOverflow
    }, /*#__PURE__*/React__default.createElement(TabGroupWrapper, {
      ref: this.navRef
    }, !(collapseHeaderWithOneTab && tabs && tabs.length < 2) && /*#__PURE__*/React__default.createElement(TabGroup, {
      onKeyDown: event => this.handleKeyDown(event),
      ref: this.tabGroupRef,
      role: "tablist"
    }, tabs, navChildren, /*#__PURE__*/React__default.createElement(TabRemainSpace, null)))), /*#__PURE__*/React__default.createElement(CardBodyStyled, {
      padding: paddingValue
    }, tabContent)));
  }
}
_defineProperty(TabbedCard, "contextType", ThemeContext);
_defineProperty(TabbedCard, "propTypes", {
  /*
   * The contents of the tabbed card. Must use the `<TabbedCardItem />` component.
   */
  children: PT.node.isRequired,
  /**
   * Causes the tabs in the header to not render when only a single tab is present.
   */
  collapseHeaderWithOneTab: PT.bool,
  /**
   * Optional nav content that will display to the right of the tabs.
   */
  navChildren: PT.node,
  /**
   * The value of the selected tab (must match one of the TabbedCardItem values).
   */
  selectedTab: PT.string.isRequired,
  /**
   * This function returns the value of the tab when changed event is fired.
   */
  onChange: PT.func.isRequired,
  /**
   * Use to easily override the default theme padding for this card.
   */
  padding: PT.oneOfType([PT.number, PT.oneOf(['none', 'micro', 'mini', 'small', 'base', 'medium', 'large', 'xlarge', 'xxlarge'])])
});
_defineProperty(TabbedCard, "defaultProps", {
  collapseHeaderWithOneTab: false,
  navChildren: null,
  padding: null
});

export { TabbedCard as T };
//# sourceMappingURL=TabbedCard-CK9lnd8M.js.map
